import { FC, ReactNode, useRef, useState } from "react";
import clsx from "clsx";
import { connect } from "react-redux";
import {
  Scan,
  RotateCcw,
  Pencil,
  Layers,
  ChevronUp,
  Bookmark,
  ChevronDown,
  Check,
  ListFilter,
  Trash2,
} from "lucide-react";
import Modal from "@albertsons/uds/molecule/Modal";
import { Button } from "@albertsons/uds/molecule/Button";
import Input from "@albertsons/uds/molecule/Input";
import { AppDispatch, RootState } from "../../../store";
import {
  floatingResetAllAction,
  addBookmark,
  onSelectBookmark,
  filterAccordianOpenAction,
  onDeleteBookmark,
  onRenameBookmark,
} from "../../../store/modules/dashboard";
import {
  resetExpandModeDCStoreFilter,
  resetExpandModeStoreOrderFilter,
} from "../../../store/modules/orderHistory";
import { Backdrop } from "../../../components";
import ResetModal from "../ResetModal";
import { isMobile } from "../../../utils/isMobile";
import {
  blockBodyScroll,
  releaseBodyScroll,
} from "../../../utils/blockScrolling";
import useWindowBreakpoints from "../../../hooks/useWindowBreakpoints";
import { IBookmark } from "../../../types/bookmark.types";
import "./styles.css";

interface IMenuItemProps {
  icon: ReactNode;
  label: string;
  dataTestId?: string;
  disabled?: boolean;
  onClick?: () => void;
}

const MenuItem: FC<IMenuItemProps> = ({
  icon,
  label,
  dataTestId,
  disabled = false,
  onClick,
}) => {
  const { screen } = useWindowBreakpoints();

  return (
    <div
      className={clsx(
        "h-12 flex flex-1 items-center justify-center",
        screen.isMobile ? "flex-col" : "gap-1",
        disabled ? "opacity-40" : "cursor-pointer"
      )}
      onClick={disabled ? undefined : onClick}
      data-testid={dataTestId}
    >
      {icon}
      <div
        className={clsx(
          "text-center",
          screen.extraSmall ? "text-xs" : "text-sm"
        )}
      >
        {label}
      </div>
    </div>
  );
};

interface IFloatingMenuProps {
  forceScreen?: "extraSmall" | "mobile" | "desktop";
  canScanBarcode?: boolean;
  showPagesButton?: boolean;
  showAnnotateButton?: boolean;
  showExpandButton?: boolean;
  isAnnotating?: boolean;
  openAnnotateModal?: () => void;
  openBarcodeModal: () => void;
  floatingResetAll: () => void;
  bookmarks: IBookmark[];
  addBookmark: (name: string) => void;
  onDeleteBookmark: (index: number) => void;
  onRenameBookmark: (value: string, index: number) => void;
  onSelectBookmark: (index: number) => void;
  selectedBookmarkId: number | null;
  isBookmarkFilled: boolean;
  floatingMenuChange: boolean;
  filterAccordianOpen: boolean;
  filterAccordianOpenAction: (value: boolean) => void;
  resetExpandModeDCStoreFilters: () => void;
  resetExpandModeStoreOrderFilters: () => void;
}

const FloatingMenu: FC<IFloatingMenuProps> = (props) => {
  const {
    forceScreen,
    canScanBarcode = true,
    isAnnotating = false,
    showPagesButton = false,
    showAnnotateButton = false,
    showExpandButton = false,
    openAnnotateModal,
    openBarcodeModal,
    floatingResetAll,
    bookmarks,
    addBookmark,
    onDeleteBookmark,
    onRenameBookmark,
    onSelectBookmark,
    selectedBookmarkId,
    isBookmarkFilled,
    floatingMenuChange,
    filterAccordianOpen,
    filterAccordianOpenAction,
    resetExpandModeDCStoreFilters,
    resetExpandModeStoreOrderFilters,
  } = props;

  const [longPressBookmarkIdx, setLongPressBookmarkIdx] = useState(-1);
  const [expandedMenu, setExpandedMenu] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarkName, setBookmarkName] = useState("");
  const [showEditBookmarkPopup, setShowEditBookmarkPopup] = useState(false);
  const [showDeleteBookmarkPopup, setShowDeleteBookmarkPopup] = useState(false);

  const isLongPressActiveRef = useRef(false);

  const isActualMobile = forceScreen === "mobile" || isMobile();

  const { getScreen } = useWindowBreakpoints();
  const screen = getScreen(forceScreen);

  const openResetModal = () => {
    setShowResetModal(true);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
  };

  const onReset = () => {
    floatingResetAll();
    closeResetModal();
    resetExpandModeDCStoreFilters();
    resetExpandModeStoreOrderFilters();
  };

  const openExpandedMenu = () => {
    setExpandedMenu(true);
    blockBodyScroll();
  };

  const closeExpandedMenu = () => {
    setExpandedMenu(false);
    releaseBodyScroll();
  };

  const onAnnotateAction = () => {
    closeExpandedMenu();
    openAnnotateModal?.();
  };

  const onBookmarkAction = () => {
    setShowBookmarks(true);
  };

  const closeBookmarkAction = () => {
    setShowBookmarks(false);
    setLongPressBookmarkIdx(-1);
  };

  const onAddBookmark = () => {
    setLongPressBookmarkIdx(-1);
    setBookmarkName(`View ${bookmarks.length + 1}`);
    setShowEditBookmarkPopup(true);
  };

  const onEditBookmark = (name: string) => {
    setBookmarkName(name);
    setShowEditBookmarkPopup(true);
  };

  const onSaveBookmark = () => {
    if (longPressBookmarkIdx > -1) {
      onRenameBookmark(bookmarkName, longPressBookmarkIdx);
    } else {
      addBookmark(bookmarkName);
      closeBookmarkAction();
    }
  };

  const onToggleBookmark = (index: number) => {
    // Prevent normal click if just long pressed
    if (isLongPressActiveRef.current) {
      return;
    }
    onSelectBookmark(index);
    closeBookmarkAction();
  };

  const onLongPressBookmark = (index: number) => {
    let timer: NodeJS.Timeout;
    const handlePointerUp = () => {
      clearTimeout(timer);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointerleave", handlePointerUp);
      setTimeout(() => {
        isLongPressActiveRef.current = false;
      }, 100);
    };
    timer = setTimeout(() => {
      setLongPressBookmarkIdx(index);
      isLongPressActiveRef.current = true;
    }, 500);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointerleave", handlePointerUp);
  };

  const filterAccordian = () => {
    filterAccordianOpenAction(true);
  };

  if (isAnnotating || filterAccordianOpen) {
    return null;
  }

  return (
    <>
      <Backdrop enabled isOpen={showBookmarks} onClose={closeBookmarkAction}>
        <div
          className={clsx(
            "fixed bottom-0 left-0 right-0 bg-zinc-50 rounded-lg shadow-2xl mt-2 mx-2 mb-4 floating-menu-wrapper z-10",
            isActualMobile && "mb-[72px]"
          )}
        >
          <div className="m-2 px-2">
            <div
              className="flex items-center text-base font-medium py-4"
              onClick={closeBookmarkAction}
            >
              <div className="pr-3">
                <ChevronDown />
              </div>
              <span>Saved Views</span>
            </div>
            <div
              className="flex items-center text-sm py-2 pl-3 select-none cursor-pointer"
              onClick={onAddBookmark}
              data-testid="addBookmark"
            >
              <div className="pr-2">
                <Bookmark className="pr-1" />
              </div>
              Add View
            </div>
            {bookmarks.map((bookmark, index) => (
              <div
                key={bookmark.id}
                className="flex justify-between items-center text-sm pl-3 select-none gap-2 active:bg-gray-100 rounded-md cursor-pointer"
              >
                <div
                  className="flex items-center py-2 w-full"
                  onClick={() => onToggleBookmark(index)}
                  onPointerDown={() => onLongPressBookmark(index)}
                  data-testid={`toggleBookmark${index}`}
                >
                  <div className="pr-2">
                    <Bookmark
                      className={clsx(
                        "pr-1",
                        selectedBookmarkId === bookmark.id && "fill-black"
                      )}
                    />
                  </div>
                  {bookmark.name}
                </div>
                <div className="flex items-center gap-1 py-2">
                  {longPressBookmarkIdx === index && (
                    <>
                      <Pencil
                        className="pr-1"
                        onClick={() => onEditBookmark(bookmark.name)}
                        data-testid={`editBookmark${index}`}
                      />
                      <Trash2
                        className="pr-1"
                        onClick={() => setShowDeleteBookmarkPopup(true)}
                        data-testid={`deleteBookmark${index}`}
                      />
                    </>
                  )}
                  {selectedBookmarkId === bookmark.id && (
                    <Check className="pr-1" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Backdrop>
      {!showBookmarks && (
        <Backdrop
          enabled={expandedMenu}
          isOpen={expandedMenu}
          onClose={closeExpandedMenu}
        >
          <div
            className={clsx(
              "fixed bottom-0 left-0 right-0 bg-zinc-50 rounded-lg shadow-2xl mt-2 mx-2 mb-4 floating-menu-wrapper z-10",
              isActualMobile && expandedMenu && "mb-[72px]"
            )}
          >
            {!floatingMenuChange ? (
              <div className="flex justify-between items-center m-2">
                <div className="w-full max-w-screen-lg">
                  <div
                    className={clsx(
                      "h-12 flex flex-1 items-center justify-center",
                      screen.isMobile && "mt-2"
                    )}
                  >
                    <MenuItem
                      disabled={!canScanBarcode}
                      icon={<Scan size={screen.extraSmall ? 18 : 24} />}
                      label="Barcode"
                      dataTestId="scan-button"
                      onClick={openBarcodeModal}
                    />
                    <MenuItem
                      icon={<RotateCcw size={screen.extraSmall ? 18 : 24} />}
                      label="Reset"
                      dataTestId="resetIcon"
                      onClick={openResetModal}
                    />
                    {showAnnotateButton && (
                      <MenuItem
                        icon={<Pencil size={screen.extraSmall ? 18 : 24} />}
                        label="Annotate"
                        dataTestId="annotate-button"
                        onClick={onAnnotateAction}
                      />
                    )}
                    {showPagesButton && (
                      <MenuItem
                        disabled
                        icon={<Layers size={screen.extraSmall ? 18 : 24} />}
                        label="Pages"
                      />
                    )}
                    {showExpandButton ? (
                      <>
                        {!expandedMenu && (
                          <MenuItem
                            icon={
                              <ChevronUp size={screen.extraSmall ? 18 : 24} />
                            }
                            label="More"
                            dataTestId="more-button"
                            onClick={openExpandedMenu}
                          />
                        )}
                      </>
                    ) : (
                      <MenuItem
                        icon={
                          <Bookmark
                            size={screen.extraSmall ? 18 : 24}
                            className={isBookmarkFilled ? "fill-black" : ""}
                          />
                        }
                        label="Saved Views"
                        dataTestId="bookmark"
                        onClick={onBookmarkAction}
                      />
                    )}
                  </div>
                  {expandedMenu && (
                    <div className="w-full max-w-screen-lg">
                      <div className="flex-1 h-12 flex items-center justify-center mt-6">
                        <MenuItem
                          icon={
                            <Bookmark
                              size={screen.extraSmall ? 18 : 24}
                              className={isBookmarkFilled ? "fill-black" : ""}
                            />
                          }
                          label="Saved Views"
                          dataTestId="bookmark"
                          onClick={onBookmarkAction}
                        />
                        <MenuItem icon="" label="" />
                        <MenuItem icon="" label="" />
                        <MenuItem icon="" label="" />
                      </div>
                      <div
                        className="flex items-center justify-center cursor-pointer"
                        onClick={closeExpandedMenu}
                      >
                        <MenuItem
                          icon={<ChevronDown />}
                          label=""
                          dataTestId="close-expand"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center m-2">
                <MenuItem
                  icon={<Pencil size={screen.extraSmall ? 18 : 24} />}
                  label="Annotate"
                  dataTestId="annotate-button"
                  onClick={onAnnotateAction}
                />
                <MenuItem icon="" label="" />
                <MenuItem icon="" label="" />
                <MenuItem
                  icon={<RotateCcw size={screen.extraSmall ? 18 : 24} />}
                  label="Reset"
                  onClick={openResetModal}
                />
                <MenuItem
                  icon={<ListFilter size={screen.extraSmall ? 18 : 24} />}
                  label="Filters"
                  onClick={filterAccordian}
                  dataTestId="filter-accordian"
                />
              </div>
            )}
          </div>
        </Backdrop>
      )}
      <ResetModal
        showResetModal={showResetModal}
        closeResetModal={closeResetModal}
        onReset={onReset}
        content="Filters, slicers, and other data view changes you've made will be cleared. Are you sure you want to continue?"
      />
      <Modal
        isOpen={showEditBookmarkPopup}
        onClose={() => setShowEditBookmarkPopup(false)}
        minHeight={20}
        minWidth={20}
        width={320}
        maxWidth="calc(100vw - 40px)"
        className="bg-white overflow-hidden !rounded-lg"
      >
        {({ close }) => (
          <div
            className="flex flex-col justify-between bg-white rounded-lg border border-gray-204 p-5"
            data-testid={`${longPressBookmarkIdx > -1 ? 'edit': 'add'}-bookmark-modal`}
          >
            <p className="font-semibold text-lg mb-2">
              {longPressBookmarkIdx > -1 ? "Rename View" : "Add View"}
            </p>
            <Input
              type="text"
              name="bookmarkName"
              autoFocus
              placeholder="Enter view name..."
              value={bookmarkName}
              onChange={(e) => setBookmarkName(e.target.value)}
            />
            <span className="flex justify-between mt-4">
              <span>
                <Button
                  variant="tertiary"
                  onClick={close}
                  className="!w-20 text-black bg-gray-200 hover:bg-gray-100 active:bg-gray-100"
                  data-testid="cancelButton"
                >
                  Cancel
                </Button>
              </span>
              <span>
                <Button
                  variant="tertiary"
                  className="!w-20 text-black bg-[#c3e1ff] disabled:bg-[#c3e1ff]"
                  onClick={() => {
                    onSaveBookmark();
                    close();
                  }}
                  disabled={bookmarkName.trim() === ""}
                  data-testid="warn-popup-close-confirm-button"
                >
                  {longPressBookmarkIdx > -1 ? "Save" : "Add"}
                </Button>
              </span>
            </span>
          </div>
        )}
      </Modal>
      <Modal
        isOpen={showDeleteBookmarkPopup}
        onClose={() => setShowDeleteBookmarkPopup(false)}
        minHeight={20}
        minWidth={20}
        width={320}
        maxWidth="calc(100vw - 40px)"
        className="bg-white overflow-hidden !rounded-lg"
      >
        {({ close }) => (
          <div
            className="flex flex-col justify-between bg-white rounded-lg border border-gray-204 p-5"
            data-testid="delete-bookmark-modal"
          >
            <p className="font-semibold text-lg text-center mb-5">
              Are you sure you want to delete this view?
            </p>
            <span className="flex justify-between">
              <span>
                <Button
                  variant="tertiary"
                  onClick={close}
                  className="!w-20 text-black bg-gray-200 hover:bg-gray-100 active:bg-gray-100"
                  data-testid="cancelButton"
                >
                  Cancel
                </Button>
              </span>
              <span>
                <Button
                  variant="tertiary"
                  className="!w-20 text-black bg-[#c3e1ff]"
                  onClick={() => {
                    onDeleteBookmark(longPressBookmarkIdx);
                    setLongPressBookmarkIdx(-1);
                    close();
                  }}
                  data-testid="warn-popup-close-confirm-button"
                >
                  Delete
                </Button>
              </span>
            </span>
          </div>
        )}
      </Modal>
    </>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  floatingResetAll: () => dispatch(floatingResetAllAction()),
  addBookmark: (name: string) => dispatch(addBookmark(name)),
  onDeleteBookmark: (index: number) => dispatch(onDeleteBookmark(index)),
  onRenameBookmark: (value: string, index: number) =>
    dispatch(onRenameBookmark(value, index)),
  onSelectBookmark: (index: number) => dispatch(onSelectBookmark(index)),
  filterAccordianOpenAction: (value: boolean) =>
    dispatch(filterAccordianOpenAction(value)),
  resetExpandModeDCStoreFilters: () => dispatch(resetExpandModeDCStoreFilter()),
  resetExpandModeStoreOrderFilters: () =>
    dispatch(resetExpandModeStoreOrderFilter()),
});

const mapStateToProps = (state: RootState) => ({
  bookmarks: state.dashboard.bookmarks,
  selectedBookmarkId: state.dashboard.selectedBookmarkId,
  isBookmarkFilled: state.dashboard.isBookmarkFilled,
  floatingMenuChange: state.dashboard.floatingMenuChange,
  filterAccordianOpen: state.dashboard.filterAccordianOpen,
});

export default connect(mapStateToProps, mapDispatchToProps)(FloatingMenu);
